"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  lowercaseWords,
  wordLength,
} from "@/lib/tool-kit/text-analysis/tokens";

const RE_SPLIT_WORDS = /\s+/;

const STOP_WORDS = new Set([
  "a",
  "about",
  "all",
  "am",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "been",
  "being",
  "both",
  "but",
  "by",
  "can",
  "could",
  "did",
  "do",
  "does",
  "each",
  "every",
  "few",
  "for",
  "from",
  "had",
  "has",
  "have",
  "he",
  "her",
  "him",
  "his",
  "how",
  "i",
  "if",
  "in",
  "is",
  "it",
  "its",
  "just",
  "many",
  "may",
  "me",
  "might",
  "more",
  "most",
  "my",
  "no",
  "not",
  "of",
  "on",
  "or",
  "our",
  "shall",
  "she",
  "should",
  "so",
  "some",
  "such",
  "than",
  "that",
  "the",
  "their",
  "them",
  "they",
  "this",
  "to",
  "too",
  "us",
  "very",
  "was",
  "we",
  "were",
  "what",
  "when",
  "where",
  "which",
  "who",
  "whom",
  "will",
  "with",
  "would",
  "you",
  "your",
]);

export interface WordEntry {
  word: string;
  count: number;
  /** Share of the counted words, 0-100. Formatted at render, in the reader's language. */
  pct: number;
}

/**
 * Ranked word counts. Words are letters from any script, digits and apostrophes (curly
 * or straight), with hyphenated words kept whole; one-letter words are dropped and,
 * optionally, stop words. Ties keep the order of first appearance.
 */
export function analyzeFrequency(
  text: string,
  excludeStopWords: boolean
): WordEntry[] {
  const words = lowercaseWords(text, { groupedNumbers: true }).filter(
    (w) => wordLength(w) > 1
  );
  const filtered = excludeStopWords
    ? words.filter((w) => !STOP_WORDS.has(w))
    : words;

  const freq = new Map<string, number>();
  for (const w of filtered) {
    freq.set(w, (freq.get(w) ?? 0) + 1);
  }

  const total = filtered.length;
  return [...freq.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([word, count]) => ({
      word,
      count,
      pct: total > 0 ? (count / total) * 100 : 0,
    }));
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_FREQUENCY_COUNTER_STRINGS = {
  yourText: "Your text",
  pasteOrTypeYourText: "Paste or type your text here...",
  excludeCommonStopWordsThe: "Exclude common stop words (the, a, and, is…)",
  totalWords: "Total words",
  uniqueWords: "Unique words",
  topWord: "Top word",
  wordFrequencyTable: "Word Frequency Table",
  word: "Word",
  count: "Count",
  frequencyBar: "Frequency bar",
  showAllWords: "one:Show all {count} word|other:Show all {count} words",
  showTop25Only: "Show top 25 only",
} as const;

export default function WordFrequencyCounterWidget() {
  const [text, setText] = useState("");
  const [excludeStopWords, setExcludeStopWords] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const entries = analyzeFrequency(text, excludeStopWords);
  const displayed = showAll ? entries : entries.slice(0, 25);
  const totalWords =
    text.trim().length === 0
      ? 0
      : text.trim().split(RE_SPLIT_WORDS).filter(Boolean).length;
  const uniqueWords = entries.length;

  const strings = useToolUi(WORD_FREQUENCY_COUNTER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="freq-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="freq-input"
          onChange={(e) => {
            setText(e.target.value);
            setShowAll(false);
          }}
          placeholder={strings.pasteOrTypeYourText}
          value={text}
        />
      </div>
      {/* Option */}
      <label className="flex cursor-pointer items-center gap-3 text-sm">
        <input
          checked={excludeStopWords}
          className="h-4 w-4 accent-primary"
          onChange={(e) => setExcludeStopWords(e.target.checked)}
          type="checkbox"
        />
        <span className="text-foreground">
          {strings.excludeCommonStopWordsThe}
        </span>
      </label>
      {/* Stats */}
      {text.trim().length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.totalWords} value={fmt(totalWords)} />
          <StatCard label={strings.uniqueWords} value={fmt(uniqueWords)} />
          <StatCard label={strings.topWord} value={entries[0]?.word ?? "—"} />
        </div>
      )}
      {/* Frequency table */}
      {entries.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.wordFrequencyTable}
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-foreground">
                    #
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-foreground">
                    {strings.word}
                  </th>
                  <th className="px-4 py-2 text-right font-medium text-foreground">
                    {strings.count}
                  </th>
                  <th className="px-4 py-2 text-right font-medium text-foreground">
                    %
                  </th>
                  <th className="hidden px-4 py-2 text-left font-medium text-foreground sm:table-cell">
                    {strings.frequencyBar}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((entry, idx) => {
                  const barWidth = entries[0]
                    ? Math.round((entry.count / entries[0].count) * 100)
                    : 0;
                  return (
                    <tr
                      className="border-border border-t bg-card odd:bg-card even:bg-muted/20"
                      key={entry.word}
                    >
                      <td className="px-4 py-2 text-muted-foreground">
                        {idx + 1}
                      </td>
                      <td className="px-4 py-2 font-medium text-foreground">
                        {entry.word}
                      </td>
                      <td className="px-4 py-2 text-right text-muted-foreground">
                        {fmt(entry.count)}
                      </td>
                      <td className="px-4 py-2 text-right text-muted-foreground">
                        {fmt(entry.pct, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}
                        %
                      </td>
                      <td className="hidden px-4 py-2 sm:table-cell">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-2 rounded-full bg-primary"
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {entries.length > 25 && !showAll && (
            <button
              className="text-primary text-sm underline-offset-4 hover:underline"
              onClick={() => setShowAll(true)}
              type="button"
            >
              {tPlural(strings.showAllWords, locale, entries.length, {
                count: fmt(entries.length),
              })}
            </button>
          )}
          {showAll && entries.length > 25 && (
            <button
              className="text-primary text-sm underline-offset-4 hover:underline"
              onClick={() => setShowAll(false)}
              type="button"
            >
              {strings.showTop25Only}
            </button>
          )}
        </section>
      )}
    </>
  );
}
